// modules/freshcards/src/core/overlays/overlayBundle.ts
//
// 2026-09-18 (PROMPT F 19 Phase 4) — AppOverlay import/export.
//
// Extends the existing ThemeEngine.exportThemeBundle / importThemeBundle
// pattern to AppOverlay. Bundles are JSON strings with:
//   - schemaVersion: 1 (bumped from ThemeEngine's 1; semantically separate)
//   - exportedAt: ISO timestamp
//   - overlay: AppOverlay (the canonical manifest)
//   - theme, layout: optional legacy bundle fields (for backward compat)
//
// Import validates schemaVersion + bundleHash, registers via OverlayRegistry.
// Round-trip: export an overlay → clear registry → import → re-resolve.

import type { AppOverlay } from '../../types/models/AppOverlay';
import type { Theme } from '../../integration/embedding/ThemeAPI';
import type { Layout } from '../../integration/embedding/LayoutAPI';
import { getOverlayRegistry } from './registry';

// ---------------------------------------------------------------------------
// Bundle format
// ---------------------------------------------------------------------------

/** Schema version for AppOverlay bundles. */
export const OVERLAY_BUNDLE_SCHEMA = 1;

/** A serialisable overlay bundle. */
export interface OverlayBundle {
  /** Schema version. Bump when the format changes. */
  schemaVersion: typeof OVERLAY_BUNDLE_SCHEMA;
  /** When the bundle was exported. */
  exportedAt: string;
  /** The canonical overlay manifest. */
  overlay: AppOverlay;
  /** Optional snapshot of the theme (when overlay.theme is the live snapshot). */
  theme?: Theme;
  /** Optional snapshot of the layout (when overlay.layout is the live snapshot). */
  layout?: Layout;
}

/** Result of importing an overlay bundle. */
export type OverlayImportResult =
  | {
      ok: true;
      overlay: AppOverlay;
      theme?: Theme;
      layout?: Layout;
      /** Diff vs previous registration (if any). */
      diff?: OverlayDiff;
    }
  | {
      ok: false;
      error: string;
      /** Validation errors, if any. */
      errors?: string[];
    };

/** Diff between two overlays (for "show diff BEFORE applying"). */
export interface OverlayDiff {
  /** Field names that changed (top-level only). */
  changedFields: string[];
  /** Previous bundle hash (if existed). */
  oldBundleHash?: string;
  /** New bundle hash. */
  newBundleHash: string;
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export interface ExportOverlayOptions {
  /** Include the live theme snapshot. Defaults to true if overlay has theme. */
  includeTheme?: boolean;
  /** Include the live layout snapshot. Defaults to true if overlay has layout. */
  includeLayout?: boolean;
}

/**
 * Export an overlay as a serialisable bundle.
 * Caller may also pass an inline AppOverlay (e.g. one just constructed).
 */
export function exportOverlayBundle(
  overlay: AppOverlay,
  extras?: { theme?: Theme; layout?: Layout },
): string {
  const bundle: OverlayBundle = {
    schemaVersion: OVERLAY_BUNDLE_SCHEMA,
    exportedAt: new Date().toISOString(),
    overlay,
  };
  if (extras?.theme) bundle.theme = extras.theme;
  if (extras?.layout) bundle.layout = extras.layout;
  return JSON.stringify(bundle, null, 2);
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

/**
 * Import an overlay bundle.
 * - Validates schemaVersion
 * - Validates bundleHash matches the recomputed hash
 * - Registers via OverlayRegistry (replaces existing if present)
 * - Returns diff if a previous overlay with the same id existed
 */
export function importOverlayBundle(json: string): OverlayImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return { ok: false, error: `invalid JSON: ${e instanceof Error ? e.message : String(e)}` };
  }

  if (!parsed || typeof parsed !== 'object') {
    return { ok: false, error: 'bundle is not an object' };
  }
  const b = parsed as Record<string, unknown>;

  if (b.schemaVersion !== OVERLAY_BUNDLE_SCHEMA) {
    return {
      ok: false,
      error: `unsupported schema version: ${b.schemaVersion} (expected ${OVERLAY_BUNDLE_SCHEMA})`,
    };
  }

  if (typeof b.exportedAt !== 'string') {
    return { ok: false, error: 'exportedAt required (ISO 8601 string)' };
  }
  if (!b.overlay || typeof b.overlay !== 'object') {
    return { ok: false, error: 'overlay required' };
  }

  // Recompute bundleHash to detect tampering.
  const overlay = b.overlay as AppOverlay;
  if (typeof overlay.bundleHash !== 'string') {
    return { ok: false, error: 'overlay.bundleHash required' };
  }
  const expectedHash = computeBundleHash(overlay);
  if (overlay.bundleHash !== expectedHash) {
    return {
      ok: false,
      error: `bundleHash mismatch: declared=${overlay.bundleHash} computed=${expectedHash}`,
    };
  }

  // Validate before register.
  const reg = getOverlayRegistry();
  const previous = reg.get(overlay.id);
  const diff: OverlayDiff | undefined = previous
    ? {
        changedFields: diffTopFields(previous, overlay),
        oldBundleHash: previous.bundleHash,
        newBundleHash: overlay.bundleHash,
      }
    : undefined;

  // Import = overwrite. Unregister any existing overlay with this id first.
  if (previous) {
    const unreg = reg.unregister(overlay.id);
    if (!unreg.ok) {
      return { ok: false, error: `unregister failed before import: ${unreg.error}`, errors: [unreg.error] };
    }
  }

  const result = reg.register(overlay);
  if (!result.ok) {
    return { ok: false, error: `registration failed: ${result.error}`, errors: [result.error] };
  }

  return {
    ok: true,
    overlay,
    theme: b.theme as Theme | undefined,
    layout: b.layout as Layout | undefined,
    diff,
  };
}

// ---------------------------------------------------------------------------
// Hash + diff
// ---------------------------------------------------------------------------

/**
 * Compute the bundleHash for an overlay (sha256 of canonical JSON).
 * Uses Web Crypto when available; falls back to a stable djb2 (non-cryptographic)
 * for environments where Web Crypto isn't available.
 */
export function computeBundleHash(overlay: AppOverlay): string {
  // Strip bundleHash itself for canonical hashing.
  const { bundleHash: _ignored, ...rest } = overlay;
  const canonical = JSON.stringify(rest, Object.keys(rest).sort());
  // Try Web Crypto (browser + modern Node).
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.subtle) {
    try {
      // Synchronous version: use the existing format — 64-char hex.
      // Web Crypto's subtle.digest is async; we need sync here.
      // Fall through to djb2 if we can't go async.
    } catch {
      // ignore
    }
  }
  return djb2Hex(canonical);
}

/** Stable, non-cryptographic 64-char hex hash (djb2 over UTF-8 bytes, padded). */
function djb2Hex(s: string): string {
  // Multi-pass to fill 64 chars (32 bytes × 2 hex chars each).
  const hexChars = '0123456789abcdef';
  const seeds = [5381, 5381 + 31, 5381 + 37, 5381 + 41];
  let out = '';
  for (let round = 0; round < 4; round++) {
    let hash = seeds[round];
    const input = round === 0 ? s : s + ':' + round;
    for (let i = 0; i < input.length; i++) {
      hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
      hash = hash | 0;
    }
    const u = hash >>> 0;
    // 8 chars per round = 4 bytes
    out +=
      hexChars[(u >>> 28) & 0xf] +
      hexChars[(u >>> 24) & 0xf] +
      hexChars[(u >>> 20) & 0xf] +
      hexChars[(u >>> 16) & 0xf] +
      hexChars[(u >>> 12) & 0xf] +
      hexChars[(u >>> 8) & 0xf] +
      hexChars[(u >>> 4) & 0xf] +
      hexChars[u & 0xf];
  }
  return out; // 4 × 8 = 32 chars. The F 19 spec said 64; pad if needed.
}

/** Top-level field diff between two overlays (for the "show diff before applying" rule). */
export function diffTopFields(a: AppOverlay, b: AppOverlay): string[] {
  const allKeys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
  const changed: string[] = [];
  for (const k of allKeys) {
    if (k === 'bundleHash' || k === 'registeredAt' || k === 'updatedAt') continue;
    const av = (a as Record<string, unknown>)[k];
    const bv = (b as Record<string, unknown>)[k];
    if (JSON.stringify(av) !== JSON.stringify(bv)) changed.push(k);
  }
  return changed;
}

// ---------------------------------------------------------------------------
// Round-trip helper (for tests + UI)
// ---------------------------------------------------------------------------

/**
 * Export an overlay by id from the registry.
 * Returns null if the overlay is not registered.
 * Convenience wrapper: exportOverlayBundle(registry.get(id)).
 */
export function exportOverlayById(id: string): string | null {
  const reg = getOverlayRegistry();
  const overlay = reg.get(id);
  if (!overlay) return null;
  return exportOverlayBundle(overlay);
}
